import { useEffect, useRef } from 'react';
import { trackEvent as sendEvent } from '@/lib/analytics';

/**
 * Custom Hook for Analytics Tracking
 * Sendet an Google Analytics 4 (gtag) UND PostHog (nur mit Einwilligung)
 */
export const useTracking = () => {
  // Track custom events
  const trackEvent = (eventName, params = {}) => {
    sendEvent(eventName, {
      ...params,
      timestamp: new Date().toISOString()
    });
  };

  // Track page view
  const trackPageView = (pagePath) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-8Q9R1YT6JP', {
        page_path: pagePath
      });
    }
  };

  // Track CTA clicks
  const trackCTAClick = (location, ctaText, destination = '/signup') => {
    trackEvent('cta_click', {
      event_category: 'engagement',
      event_label: location,
      cta_text: ctaText,
      destination: destination,
      page: window.location.pathname
    });
  };

  // Track scroll depth
  const trackScrollDepth = (depth) => {
    trackEvent('scroll_depth', {
      event_category: 'engagement',
      event_label: `${depth}%`,
      page: window.location.pathname
    });
  };

  // Track section view (using Intersection Observer)
  const trackSectionView = (sectionName) => {
    trackEvent('section_view', {
      event_category: 'engagement',
      event_label: sectionName,
      page: window.location.pathname
    });
  };

  // Track FAQ interaction
  const trackFAQOpen = (question) => {
    trackEvent('faq_open', {
      event_category: 'engagement',
      event_label: question,
      page: window.location.pathname
    });
  };

  // Track pricing plan click
  const trackPricingClick = (plan, price) => {
    trackEvent('pricing_plan_click', {
      event_category: 'conversion',
      event_label: plan,
      value: price || 0,
      page: window.location.pathname
    });
  };

  // Track feature interaction
  const trackFeatureClick = (featureName) => {
    trackEvent('feature_click', {
      event_category: 'engagement',
      event_label: featureName,
      page: window.location.pathname
    });
  };

  // Track testimonial interaction
  const trackTestimonialView = (testimonialName) => {
    trackEvent('testimonial_view', {
      event_category: 'engagement',
      event_label: testimonialName,
      page: window.location.pathname
    });
  };

  return {
    trackEvent,
    trackPageView,
    trackCTAClick,
    trackScrollDepth,
    trackSectionView,
    trackFAQOpen,
    trackPricingClick,
    trackFeatureClick,
    trackTestimonialView
  };
};

/**
 * Hook for tracking scroll depth automatically
 */
export const useScrollTracking = () => {
  const { trackScrollDepth } = useTracking();
  const trackedDepths = useRef(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);

      // Track at 25%, 50%, 75%, 90%, 100%
      const milestones = [25, 50, 75, 90, 100];
      
      milestones.forEach(milestone => {
        if (scrollPercent >= milestone && !trackedDepths.current.has(milestone)) {
          trackedDepths.current.add(milestone);
          trackScrollDepth(milestone);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackScrollDepth]);
};

/**
 * Hook for tracking section visibility with Intersection Observer
 */
export const useSectionTracking = (sectionName, ref) => {
  const { trackSectionView } = useTracking();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!ref.current || hasTracked.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTracked.current) {
          hasTracked.current = true;
          trackSectionView(sectionName);
        }
      },
      { threshold: 0.5 } // Section is 50% visible
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [sectionName, ref, trackSectionView]);
};
